import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import axios from 'axios';

// Controle de sessão por inatividade (20 minutos)
(function setupIdleLogout() {
  if (typeof window === 'undefined') return;
  if (window.__idleLogoutInstalled) return;
  window.__idleLogoutInstalled = true;

  const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutos
  let idleTimerId;

  const shouldLogSession = () => (
    process.env.REACT_APP_SESSION_DEBUG === 'true' ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_SESSION') === '1')
  );

  const triggerLogout = () => {
    if (shouldLogSession()) {
      console.info('[SESSION] Idle logout disparado após inatividade', {
        timeoutMs: IDLE_TIMEOUT_MS,
        ts: new Date().toISOString(),
        path: window.location.pathname
      });
    }
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (_) {}
    const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/';
    if (!isLoginPage) {
      window.location.href = '/login';
    }
  };

  const resetTimer = () => {
    if (idleTimerId) clearTimeout(idleTimerId);
    idleTimerId = setTimeout(triggerLogout, IDLE_TIMEOUT_MS);
    if (shouldLogSession()) {
      console.debug('[SESSION] Atividade detectada. Temporizador reiniciado', {
        timeoutMs: IDLE_TIMEOUT_MS,
        ts: new Date().toISOString(),
      });
    }
  };

  const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
  activityEvents.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));
  if (shouldLogSession()) {
    console.info('[SESSION] Idle logout instalado', {
      timeoutMs: IDLE_TIMEOUT_MS,
      events: activityEvents
    });
  }
  resetTimer();
})();

// ==================================================================
// LIMPEZA FORÇADA DE AUTENTICAÇÃO NA INICIALIZAÇÃO DA APLICAÇÃO
// ==================================================================
(function forceLogoutOnRestart() {
  // Criar ou obter uma marca de tempo única para esta sessão do servidor
  const SERVER_SESSION_ID = "server_session_id";
  const SESSION_FILE_PATH = "/session_id.txt"; // Arquivo que contém a sessão do servidor
  
  // Verificar se já temos um ID de sessão
  const storedSessionId = localStorage.getItem(SERVER_SESSION_ID);
  
  // Adicionar timestamp para evitar cache do navegador
  const timestampedPath = SESSION_FILE_PATH + '?t=' + new Date().getTime();
  
  // Buscar o ID de sessão atual do servidor
  fetch(timestampedPath)
    .then(response => {
      if (!response.ok) {
        throw new Error("Não foi possível carregar o ID de sessão");
      }
      return response.text();
    })
    .then(serverSessionId => {
      // Remover espaços em branco que podem causar problemas na comparação
      serverSessionId = serverSessionId.trim();
      
      // Verificar se há token válido antes de forçar logout
      const hasValidToken = localStorage.getItem('token');
      
      // Se não temos um ID armazenado, apenas armazenar o novo (primeira vez)
      if (!storedSessionId) {
        localStorage.setItem(SERVER_SESSION_ID, serverSessionId);
      } 
      // Se é diferente E não temos token válido, então forçar logout
      else if (storedSessionId !== serverSessionId && !hasValidToken) {

        
        // VERIFICAR NOVAMENTE se token foi criado após primeira verificação
        const hasValidTokenNow = localStorage.getItem('token');
        const tokenTime = localStorage.getItem('tokenCreatedAt');
        const now = Date.now();
        const tokenAge = tokenTime ? (now - parseInt(tokenTime)) : Infinity;
        
        // Se token foi criado há menos de 10 segundos OU token existe agora, não limpar
        if (hasValidTokenNow || tokenAge <= 10000) {
          localStorage.setItem(SERVER_SESSION_ID, serverSessionId);
        } else {
        localStorage.clear();
          localStorage.setItem(SERVER_SESSION_ID, serverSessionId);
        }
        
        // Armazenar o novo ID de sessão
        localStorage.setItem(SERVER_SESSION_ID, serverSessionId);
        
        // Redirecionar para login se não estiver já na página de login
        const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/';
        if (!isLoginPage) {
          window.location.href = '/login';
        }
      } else {

        // Atualizar ID se necessário
        if (storedSessionId !== serverSessionId) {

          localStorage.setItem(SERVER_SESSION_ID, serverSessionId);
        }
      }
      

    })
    .catch(error => {
      console.error("Erro ao verificar sessão:", error);
      // Em caso de erro, assumimos que não conseguimos verificar, mas não limparemos
      // o token para evitar logout desnecessário em caso de erro temporário
    });
})();

// Configuração global do Axios
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Adicionar o ID de sessão ao cabeçalho para verificação no backend
  const SERVER_SESSION_ID = "server_session_id";
  const sessionId = localStorage.getItem(SERVER_SESSION_ID);
  if (sessionId) {
    config.headers['X-Session-ID'] = sessionId;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// DESABILITADO: Interceptor causa conflito - apenas verificação de sessão ativa
// axios.interceptors.response.use((response) => {
//   return response;
// }, (error) => {
//   return Promise.reject(error);
// });

// Esperar o DOM estar completamente carregado
document.addEventListener('DOMContentLoaded', () => {
  // Limpar qualquer conteúdo existente no corpo do documento
  // exceto o elemento root
  const bodyElement = document.body;
  const rootElement = document.getElementById('root');
  
  if (rootElement) {
    // Limpar qualquer HTML indesejado
    while (bodyElement.firstChild) {
      if (bodyElement.firstChild === rootElement) {
        break;
      }
      bodyElement.removeChild(bodyElement.firstChild);
    }
    
    // Limpar qualquer texto não desejado dentro do corpo
    const childNodes = Array.from(bodyElement.childNodes);
    for (let node of childNodes) {
      if (node !== rootElement && node.nodeType === Node.TEXT_NODE) {
        bodyElement.removeChild(node);
      }
    }
    
    // Renderizar a aplicação React
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </React.StrictMode>
    );
  } else {
    console.error('Elemento root não encontrado');
    
    // Criar um novo elemento root se necessário
    const newRoot = document.createElement('div');
    newRoot.id = 'root';
    
    // Limpar o body e adicionar apenas o novo root
    while (bodyElement.firstChild) {
      bodyElement.removeChild(bodyElement.firstChild);
    }
    
    bodyElement.appendChild(newRoot);
    
    // Renderizar a aplicação no novo root
    const root = ReactDOM.createRoot(newRoot);
    root.render(
      <React.StrictMode>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </React.StrictMode>
    );
  }
});
